import Loading from 'quasar/src/plugins/loading/Loading.js';import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import configCloud from 'src/apps/cloud/configCloud.ts';
import logger from 'src/utils/logger';
import { CloudWSClient } from 'src/apps/cloud/services/wsClient';
import { HandledError } from 'src/apps/cloud/services/wsErrors';
export { HandledError };
class WSClass {
    $q;
    t;
    wsMessageListener = null;
    messageListenerWS = null;
    wsClientBound = false;
    everConnected = false;
    reconnectOverlayShown = false;
    noticeHandlers = {
        onConnectServer: (m) => this.onConnectServer(m),
        onDisconnectServer: (m) => this.onDisconnectServer(m),
        monitors: (m) => this.monitors(m),
        getDeviceList: (m) => this.getDeviceList(m),
        farmStatistics: (m) => this.farmStatistics(m),
        netScaner: (m) => this.netScaner(m)
    };
    constructor(t) {
        this.$q = useQuasar();
        this.t = t || ((key) => key);
    }
    /**
     * Соединение с сервером облака
     * @return boolean
     */
    async connect() {
        const client = this.getWSClient();
        this.bindWSClient(client);
        const connected = await client.connect();
        this.$q.ws = client.socket;
        return connected;
    }
    disconnect() {
        if (this.reconnectOverlayShown) {
            Loading.hide();
            this.reconnectOverlayShown = false;
        }
        this.wsMessageListener = null;
        this.messageListenerWS = null;
        const client = this.$q.wsClient;
        client?.disconnect();
        this.$q.ws = null;
    }
    /**
     * Validate WebSocket message structure to prevent prototype pollution
     */
    isValidWSMessage(msg) {
        if (typeof msg !== 'object' || msg === null)
            return false;
        const obj = msg;
        // Check for prototype pollution attempts (check own properties only)
        if (Object.prototype.hasOwnProperty.call(obj, '__proto__') ||
            Object.prototype.hasOwnProperty.call(obj, 'constructor') ||
            Object.prototype.hasOwnProperty.call(obj, 'prototype')) {
            logger.warn('Potential prototype pollution attempt detected');
            return false;
        }
        // Validate required structure
        if (typeof obj.type !== 'string')
            return false;
        const validTypes = ['notice', 'answer', 'error', 'query'];
        if (!validTypes.includes(obj.type))
            return false;
        return true;
    }
    /**
     * Обработчики поступающих сообщений от ws
     */
    setListeners() {
        if (!this.$q.ws)
            return;
        // Re-attach listener if WS instance changed (reconnect).
        if (this.wsMessageListener && this.messageListenerWS && this.messageListenerWS !== this.$q.ws) {
            try {
                this.messageListenerWS.onMessage.removeListener(this.wsMessageListener);
            }
            catch (e) {
                logger.debug('WS: failed to detach previous message listener', e);
            }
            this.wsMessageListener = null;
        }
        if (this.wsMessageListener)
            return;
        this.wsMessageListener = (message) => {
            const parsed = typeof message === 'string'
                ? (() => {
                    try {
                        return JSON.parse(message, (key, value) => {
                            if (key === '__proto__' || key === 'constructor' || key === 'prototype')
                                return undefined;
                            return value;
                        });
                    }
                    catch (e) {
                        logger.error('Failed to parse WebSocket message:', e);
                        return null;
                    }
                })()
                : message;
            if (!parsed || !this.isValidWSMessage(parsed)) {
                logger.warn('Invalid WebSocket message structure');
                return;
            }
            const msg = parsed;
            // Исключаем ответы от request
            if (msg.type === 'answer')
                return;
            /** NOTICE */
            if (msg.type === 'notice' && msg.data && typeof msg.data === 'object') {
                const event = msg.data.event;
                if (typeof event !== 'string')
                    return;
                logger.log('Notice received:', event);
                try {
                    const handler = this.noticeHandlers[event];
                    if (handler) {
                        handler(msg);
                    }
                    else {
                        logger.log('No handler for event:', event);
                    }
                }
                catch (e) {
                    logger.error('Error handling notice event:', e);
                }
            }
        };
        this.$q.ws.onMessage.addListener(this.wsMessageListener);
        this.messageListenerWS = this.$q.ws;
    }
    getWSClient() {
        if (!this.$q.wsClient) {
            this.$q.wsClient = new CloudWSClient({
                url: configCloud.ws.url,
                translate: this.t
            });
        }
        return this.$q.wsClient;
    }
    bindWSClient(client) {
        if (this.wsClientBound)
            return;
        this.wsClientBound = true;
        client.onOpen((ws) => {
            this.$q.ws = ws;
            this.setListeners();
            if (this.reconnectOverlayShown) {
                Loading.hide();
                this.reconnectOverlayShown = false;
            }
            this.everConnected = true;
            // Re-auth only on reconnects (when user is already known in store).
            const token = sessionStorage.getItem('token');
            if (token && this.$q.store?.app?.user) {
                void this.$q.class.User.loginWithToken(token).catch((e) => {
                    logger.warn('Re-authentication failed:', e);
                });
            }
        });
        client.onClose(() => {
            if (!this.everConnected)
                return;
            if (this.reconnectOverlayShown)
                return;
            this.reconnectOverlayShown = true;
            Loading.show({
                message: 'Connection lost. Reconnecting…',
                spinnerColor: 'white'
            });
        });
        client.onReconnectAttempt(({ attempt, maxAttempts, delayMs }) => {
            if (!this.everConnected)
                return;
            this.reconnectOverlayShown = true;
            Loading.show({
                message: `Reconnecting… ${attempt}/${maxAttempts} (next in ${Math.ceil(delayMs / 1000)}s)`,
                spinnerColor: 'white'
            });
        });
        client.onReconnectExhausted(() => {
            if (this.reconnectOverlayShown) {
                Loading.hide();
                this.reconnectOverlayShown = false;
            }
            this.$q.store.app.user = null;
            sessionStorage.removeItem('token');
            this.$q.notify({
                type: 'negative',
                message: 'Connection lost. Please log in again.'
            });
        });
    }
    async onConnectServer(message) {
        const serverID = Number(message.data.server_id);
        if (Number.isNaN(serverID))
            return;
        this.$q.store.app.setServerOnline(serverID, true);
        this.$q.store.farm.setCurrentServerOnline(serverID, true);
        if (this.$q.store.farm.server?.id === serverID) {
            this.$q.class.Server.getStatus(serverID);
        }
    }
    onDisconnectServer(message) {
        const serverID = Number(message.data.server_id);
        if (Number.isNaN(serverID))
            return;
        logger.log('onDisconnectServer', serverID);
        this.$q.store.app.setServerOnline(serverID, false);
        this.$q.store.farm.setCurrentServerOnline(serverID, false);
    }
    monitors(message, fromScan) {
        const serverID = Number(message.data.server_id || message.data.farm_id);
        if (Number.isNaN(serverID))
            return;
        try {
            this.$q.store.app.updateServerStatistics(serverID, message.data.data.statistics);
        }
        catch (e) {
            logger.debug('WS monitors: failed to set server statistics', e);
        }
        if (message.data.data.devices && this.$q.store.farm.server?.id === serverID) {
            if (typeof message.data.data.devices === 'object') {
                const devices = [];
                Object.entries(message.data.data.devices).forEach(([ip, props]) => {
                    const device = this.$q.class.Device.formatDeviceData(ip, null, props);
                    devices.push(device);
                });
                this.$q.store.farm.applyServerDevicesSnapshot(serverID, devices, !!fromScan);
            }
            else if (message.data.data.devices === 'done') {
                this.$q.store.farm.applyServerDevicesSnapshot(serverID, 'done', !!fromScan);
            }
        }
    }
    getDeviceList(message) {
        this.monitors(message, true);
    }
    farmStatistics(message) {
        logger.log('farmStatistics received:', message.data);
        if (!message.data.data)
            return;
        this.$q.store.app.applyFarmStatisticsBatch(message.data.data);
        if (!this.$q.store.farm.farm)
            return;
        const currentFarmId = this.$q.store.farm.farm.id;
        const currentFarm = message.data.data.find((d) => Number(d.farm_id) === currentFarmId);
        if (!currentFarm || typeof currentFarm.devices !== 'object')
            return;
        const devices = [];
        Object.entries(currentFarm.devices).forEach(([id, props]) => {
            const deviceProps = props;
            const device = this.$q.class.Device.formatDeviceData(deviceProps.ip, parseInt(id), deviceProps);
            devices.push(device);
        });
        this.$q.store.farm.applyFarmDevicesSnapshot(currentFarmId, devices);
    }
    // noinspection SpellCheckingInspection
    netScaner(message) {
        if (!message.data.server_id)
            return;
        this.$q.store.farm.applyNetScannerProgress(message.data.server_id, message.data.data.progress);
    }
    minerStatistics() {
    }
}
export default WSClass;
